/**
 * Per-tile "Needs attention" chip rendered in the top-right of a video
 * source tile when `deriveChipState` returns a non-null state.
 *
 * Visual contract — yellow accent bar, dark background, deviceName
 * text + underlined actionLink — is locked by the Chip system design.
 * The chip is the user-facing surface for the per-aspect
 * AuthenticationStatus / connectionStatus failures documented in the
 * chip-state cascade:
 *
 *   - Diagnostic verbs ("Authenticate" / "Reconnect") render as an
 *     underlined action link
 *   - Single warning severity → one yellow accent bar, no other color
 *     variants in P1
 *   - Single chip per tile (priority cascade in `chip-state.ts`)
 *
 * The chip is keyboard-actionable (`role="button"`, `tabIndex={0}`,
 * descriptive `aria-label`) and the click stops propagation so the
 * parent card's selection toggle does not fire.
 *
 * Rendered-testable in isolation — `<Item>` wires the live store /
 * derivation, but the visual contract is verified here without
 * dragging in the full state-sync graph.
 */
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { ChipState } from './chip-state.js';

export interface NeedsAttentionChipProps {
  /** Pure-derived chip state (see `chip-state.ts`). */
  state: ChipState;

  /** Subset of the device used for the chip's display string. */
  device: { name: string };

  /** Optional CSS overrides for positioning at the call site. */
  className?: string;

  /** Click handler — typically opens device settings. */
  onClick: () => void;
}

/**
 * Render the chip. Position is set by the caller via `className`
 * (item.tsx places it `absolute right-8 top-8`). Internal styling is
 * the visual contract — accent bar, background, link underline.
 */
export const NeedsAttentionChip = ({
  state,
  device,
  className,
  onClick
}: NeedsAttentionChipProps): ReactElement => {
  // Stop propagation so clicking the chip doesn't bubble up to the
  // card (which would trigger card-level selection or default
  // behavior). Click must stop propagation.
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    onClick();
  };

  // Keyboard activation parity — Space and Enter behave like a button.
  // The chip is fully keyboard-operable per Design peer review.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }
  };

  return (
    <div
      role='button'
      tabIndex={0}
      aria-label={state.actionLink}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        // Layout: accent bar (left) + content. flex-row so the bar sits
        // next to the text. rounded matches existing badges in this tile
        // surface (auto-tracking, lock).
        'flex flex-row items-center overflow-hidden rounded',
        // Cursor + focus styling so the keyboard ring is visible.
        'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-warning',
        // Dark background per the Chip system design.
        'bg-base-100/90 text-white',
        className
      )}
    >
      {/* Yellow accent bar (left edge). Per the Chip system design —
          uses Tailwind theme `warning` token to honor the whitelabel
          variant constraint (ui-architecture rule).
          `self-stretch` (instead of `h-full`) so the bar inherits the
          flex line's cross-axis size — `h-full` doesn't resolve when
          the parent flex container's height is auto (determined by
          siblings), which made the bar invisible.
          Arbitrary width `w-[4px]`: the design-system tailwind config
          restricts the `width` scale to fractions + `full`/`auto`/etc.
          (packages/design-system/src/tailwind/config.cjs ~line 91),
          so `w-1` / `w-2` simply don't generate CSS and the bar
          rendered at width=0 even with `bg-warning` set. */}
      <div aria-hidden='true' className='w-[4px] self-stretch bg-warning' />

      {/* Text payload: deviceName (compact) + underlined verb. */}
      <div className='flex flex-row items-center gap-4 px-8 py-4 text-[12px]'>
        <span className='font-medium'>{device.name}</span>
        <span className='underline'>{state.actionLink}</span>
      </div>
    </div>
  );
};
